import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { PhoneAdvice } from "@/components/molecules/PhoneAdvice/PhoneAdvice";

type Props = {
    onNext: () => void;
    onBack?: () => void;
    isFirstStep: boolean;
    isLastStep: boolean;
    nextDisabled?: boolean;
    statusText?: string;
    nextButtonText?: string | null;
    finishButtonText?: string | null;
    phoneNumber?: string | null;
};

export function BottomBar({
    onNext,
    onBack,
    isFirstStep,
    isLastStep,
    nextDisabled = false,
    statusText,
    nextButtonText,
    finishButtonText,
    phoneNumber,
}: Props) {
    const { t } = useTranslation();

    const status = statusText ? (
        <span className="text-sm text-gray-600 font-medium text-center">
            {statusText}
        </span>
    ) : null;

    return (
        <footer className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 shadow-lg">
            {status && (
                <div className="flex justify-center border-b border-gray-100 px-6 py-2 sm:hidden">
                    {status}
                </div>
            )}

            <div className="flex items-center justify-between px-6 py-4 sm:grid sm:grid-cols-3">
                <div className="flex items-center gap-3">
                    {phoneNumber && <PhoneAdvice phoneNumber={phoneNumber} />}
                </div>

                <div className="hidden justify-center sm:flex">{status}</div>

                <div className="flex items-center justify-end gap-4">
                    {!isFirstStep && onBack && (
                        <button
                            type="button"
                            onClick={onBack}
                            className="text-sm font-medium text-gray-600 flex items-center gap-1 hover:text-gray-900 cursor-pointer"
                            data-testid="bottom-bar-back"
                        >
                            ← {t("bottomBar.back")}
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={onNext}
                        disabled={nextDisabled}
                        className={cn(
                            "flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all whitespace-nowrap",
                            nextDisabled
                                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                : "bg-primary text-contrast-text hover:bg-primary-dark hover:scale-105 hover:shadow-md active:scale-100 cursor-pointer",
                        )}
                        data-testid="bottom-bar-next"
                    >
                        {isLastStep
                            ? (finishButtonText ?? t("bottomBar.finish"))
                            : (nextButtonText ?? t("bottomBar.next"))}
                        {!isLastStep && <span aria-hidden>→</span>}
                        {isLastStep && <span aria-hidden>✓</span>}
                    </button>
                </div>
            </div>
        </footer>
    );
}
